import {
    ADDRESS_OWNERSHIP_OWNED,
    BUSINESS_DETAILS_FIELD_KEYS,
    BUSINESS_DETAILS_KEY,
    MANDATE_APPLICANT_FIELD_KEYS,
    PINCODE_DIGIT_LENGTH,
    PRIMARY_APPLICANT_SECTION_KEY,
} from '../../MandateFormConstants';
import { isValidPincode } from '../../../../utils/utils';
import { CoApplicantRow, isApplicantEntryPresent } from './coApplicantTypes';

type Ownership = string | null | undefined;

// The owned-property block shows once at least one entered ownership is non-blank and none of
// them is exactly "Owned".
export const isOwnedPropertyBlockVisible = (ownerships: Ownership[]): boolean => {
    const chosen = ownerships.filter((value): value is string => typeof value === 'string' && value.trim() !== '');
    return chosen.length > 0 && !chosen.includes(ADDRESS_OWNERSHIP_OWNED);
};

// Shared by the primary applicant and every co-applicant row below - current ownership always
// counts, permanent only once "Permanent address same as current" is off.
const pushOwnershipPair = (ownerships: Ownership[], fields: Record<string, any>) => {
    ownerships.push(fields[MANDATE_APPLICANT_FIELD_KEYS.CURRENT_ADDRESS_OWNERSHIP]);
    if (!fields[MANDATE_APPLICANT_FIELD_KEYS.PERMANENT_SAME_AS_CURRENT]) {
        ownerships.push(fields[MANDATE_APPLICANT_FIELD_KEYS.PERMANENT_ADDRESS_OWNERSHIP]);
    }
};

// Every ownership value that will actually be saved - business, the primary applicant (only while
// its slot is active), and every present co-applicant row. Mirrors Rocket's effectiveOwnerships
// so both apps feed the owned-property visibility predicate the same inputs.
export const collectEnteredOwnerships = (
    formData: Record<string, any>,
    primarySlotActive: boolean,
    coApplicants: CoApplicantRow[],
): Ownership[] => {
    const ownerships: Ownership[] = [formData[`${BUSINESS_DETAILS_KEY}.${BUSINESS_DETAILS_FIELD_KEYS.BUSINESS_ADDRESS_OWNERSHIP}`]];

    if (primarySlotActive) {
        const primaryFields = Object.fromEntries(
            Object.values(MANDATE_APPLICANT_FIELD_KEYS).map((key) => [key, formData[`${PRIMARY_APPLICANT_SECTION_KEY}.${key}`]]),
        );
        pushOwnershipPair(ownerships, primaryFields);
    }

    coApplicants.filter(isApplicantEntryPresent).forEach((row) => pushOwnershipPair(ownerships, row));

    return ownerships;
};

// Owned-property pincode is optional but, when present, must be a well-formed 6-digit pincode -
// mirrors isApplicantPincodeValid/isBusinessPincodeValid's pattern in the sibling *Types.ts files.
export const isOwnedPropertyPincodeValid = (value: any): boolean => isValidPincode(value, PINCODE_DIGIT_LENGTH);
